import type { Channel } from '@novachat/shared';
import { ChevronLeft, MessagesSquare } from 'lucide-react';
import { useChannels } from './Sidebar';
import { Avatar } from './Avatar';
import { ChannelIcon } from './ChannelIcon';
import { useDirectory } from '../lib/directory';
import { plainText } from '../lib/mentions';
import { clock, dayLabel } from '../lib/time';
import { useChat } from '../state/chat';
import { openThread } from '../state/thread';
import { goBack } from '../state/nav';

/** "Dallar": the threads you follow, as cards, newest reply first; a card opens its thread in its channel. */
export function ThreadsPage() {
  const { data: channels = [] } = useChannels();
  const followed = useChat((s) => s.followed);
  const people = useDirectory();
  const list = Object.values(followed).sort((a, b) => (b.root.thread?.lastReplyAt ?? b.root.createdAt).localeCompare(a.root.thread?.lastReplyAt ?? a.root.createdAt));
  const where = (channelId: string) => {
    const channel: Channel | undefined = channels.find((c) => c.id === channelId);
    return !channel ? '' : channel.kind === 'dm' ? (channel.peer?.name ?? 'Direkt mesaj') : `#${channel.name}`;
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex min-h-14 flex-none items-center gap-2 border-b border-line pt-[max(8px,env(safe-area-inset-top))] pr-3 pb-2 pl-2 md:gap-3 md:pr-4 md:pl-5">
        <button type="button" aria-label="Listeye dön" onClick={goBack} className="grid size-10 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2 md:hidden">
          <ChevronLeft size={24} />
        </button>
        <MessagesSquare size={20} className="flex-none text-fg-2" aria-hidden="true" />
        <h1 className="m-0 font-heading text-[17px] font-bold">Dallar</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-2xl flex-col gap-3 px-3 py-4 md:px-5 md:py-6">
          <p className="m-0 text-[13px] leading-normal text-fg-3">Takip ettiğin konu dalları; yeni yanıtı olan en üstte. Birine tıklayınca kanalında, yanında açılır.</p>
          {list.length === 0 ? (
            <p className="m-0 rounded-lg border border-line bg-surface-2/50 p-4 text-sm text-fg-3">Takip ettiğin bir dal yok. Bir mesajda “Dalda yanıtla” deyince ya da biri seni dalda anınca burada görünür.</p>
          ) : (
              <ul aria-label="Takip ettiğin dallar" className="m-0 flex list-none flex-col gap-2 p-0">
              {list.map((thread) => {
                const channel = channels.find((c) => c.id === thread.root.channelId);
                const author = thread.root.author;
                const last = thread.root.thread?.lastReplyAt ?? thread.root.createdAt;
                const fresh = thread.unread > 0;
                return (
                  <li key={thread.root.id}>
                    <button
                      type="button"
                      onClick={() => openThread(thread.root.channelId, thread.root.id)}
                      className={`nc-press flex w-full gap-3 rounded-lg border p-3 text-left ${fresh ? 'border-brand-400/40 bg-brand-500/8 hover:bg-brand-500/12' : 'border-line bg-surface-2/50 hover:border-line-strong hover:bg-surface-2'}`}
                    >
                      <Avatar id={author?.id ?? 'unknown'} name={author?.name ?? 'Silinmiş kullanıcı'} size={36} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-xs text-fg-3">
                          {channel && (
                            <span className="inline-flex min-w-0 items-center gap-1 rounded-xs bg-surface-3 px-1.5 py-0.5 font-semibold text-fg-2">
                              <ChannelIcon channel={channel} size={12} />
                              <span className="truncate">{where(thread.root.channelId).replace(/^#/, '')}</span>
                            </span>
                          )}
                          <time dateTime={last} className="ml-auto flex-none">
                            {dayLabel(last) === 'Bugün' ? clock(last) : dayLabel(last)}
                          </time>
                        </span>
                        <span className="mt-1.5 block truncate text-[13.5px] font-bold text-fg-1">{author?.name ?? 'Silinmiş kullanıcı'}</span>
                        <span className="line-clamp-2 text-sm leading-snug text-fg-2">
                          {thread.root.deletedAt ? 'Bu mesaj silindi' : plainText(thread.root.content, people) || 'Dosya eki'}
                        </span>
                        <span className="mt-2 flex items-center gap-2 text-xs">
                          {thread.root.thread && thread.root.thread.people.length > 0 && (
                            <span className="flex flex-none -space-x-1.5" aria-hidden="true">
                              {thread.root.thread.people.slice(0, 3).map((p) => (
                                <Avatar key={p.id} id={p.id} name={p.name} size={18} />
                              ))}
                            </span>
                          )}
                          {fresh ? (
                            <span className="rounded-full bg-brand-500 px-2 py-0.5 font-bold text-white">{thread.unread} yeni yanıt</span>
                          ) : (
                            <span className="font-semibold text-fg-2">{thread.root.thread?.count ?? 0} yanıt</span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
